// ==========================================
// [DOC] FILE: src/lib/tiers.ts
// ==========================================
// Tier system for renown-based and percentile-based progression
// - Fixed tiers based on renown milestones
// - Dynamic tiers based on global user percentile
// - Utility functions for level and tier assignment
// ==========================================

export type TierKey =
  | 'lurker'
  | 'initiate'
  | 'disciple'
  | 'adept'
  | 'alchemist'
  | 'seer'
  | 'strategist'
  | 'oracle'
  | 'architect'
  | 'whale'
  | 'the1'
  | 'silenthand'

export const tiers: Record<TierKey, {
  name: string
  color: string
  description: string
  minPercentile?: number  // Only for percentile tiers
  requiredRenown?: number // Only for fixed tiers
}> = {
  // [LOCK] Fixed renown-based progression tiers
  lurker: {
    name: 'The Lurker',
    color: 'text-zinc-400',
    description: 'Unseen, unheard. Yet present. Watching the game begin.',
    requiredRenown: 0,
  },
  initiate: {
    name: 'The Initiate',
    color: 'text-blue-400',
    description: 'You have been called. The veil lifts.',
    requiredRenown: 100,
  },
  disciple: {
    name: 'The Disciple',
    color: 'text-green-400',
    description: 'You obey the signals, study the charts.',
    requiredRenown: 500,
  },
  adept: {
    name: 'The Adept',
    color: 'text-amber-400',
    description: 'You wield tools, speak in tickers.',
    requiredRenown: 2000,
  },
  alchemist: {
    name: 'The Alchemist',
    color: 'text-fuchsia-500',
    description: 'You transmute data into gain.',
    requiredRenown: 5000,
  },
  strategist: {
    name: 'The Strategist',
    color: 'text-sky-400',
    description: 'You move others. You trade with purpose.',
    requiredRenown: 10000,
  },

  // [BRAIN] Percentile-based competitive tiers
  seer: {
    name: 'The Seer',
    color: 'text-violet-400',
    description: 'You foresee market sentiment.',
    minPercentile: 90,
  },
  oracle: {
    name: 'The Oracle',
    color: 'text-red-500',
    description: 'Others seek your words.',
    minPercentile: 97,
  },
  architect: {
    name: 'The Architect',
    color: 'text-yellow-400',
    description: 'You built the framework.',
    minPercentile: 99,
  },
  whale: {
    name: 'The Whale',
    color: 'text-emerald-400',
    description: 'You are the market.',
    minPercentile: 99.5,
  },
  the1: {
    name: 'The 1%',
    color: 'text-lime-400',
    description: 'Legendary. Feared. You are the 1%.',
    minPercentile: 99.9,
  },
  silenthand: {
    name: 'The Silent Hand',
    color: 'text-white',
    description: 'Unseen. Unknown. Everything moves according to your will.',
    minPercentile: 100,
  },
}

// ==========================================
// [MATH] Leveling System — one curve, defined in src/lib/xp/curve.ts
// ==========================================
// ⭐ DELEGATES to the single source of truth (owner 2026-08-22: consolidate onto renown). This used to
// carry its own base=100/growth=1.15 loop, which disagreed with the linear floor(favor/100) math the
// navbar/profile used — the split that made the badge show the wrong level. Both now resolve through
// levelFromXp so a renown value maps to exactly one level everywhere.
export { levelFromXp as getLevelFromRenown } from '@/lib/xp/curve'

// ==========================================
// [STEP] Renown Tier - Highest unlocked by static renown
// ==========================================
export function getFixedTierFromRenown(renown: number): TierKey {
  const fixed = Object.entries(tiers)
    .filter(([, val]) => val.requiredRenown !== undefined)
    .sort((a, b) => (b[1].requiredRenown! - a[1].requiredRenown!)) as [TierKey, typeof tiers[TierKey]][]

  for (const [key, tier] of fixed) {
    if (renown >= (tier.requiredRenown ?? 0)) {
      return key
    }
  }

  return 'lurker'
}

// ==========================================
// [STATS] Percentile Tier - Based on user rank vs others
// ==========================================
export function getTierByPercentile(percentile: number): TierKey {
  const ranked = Object.entries(tiers)
    .filter(([, val]) => val.minPercentile !== undefined)
    .sort((a, b) => (a[1].minPercentile! - b[1].minPercentile!)) as [TierKey, typeof tiers[TierKey]][]

  let selected: TierKey = 'lurker'
  for (const [key, tier] of ranked) {
    if (percentile >= (tier.minPercentile ?? 0)) {
      selected = key
    }
  }

  return selected
}

// ==========================================
// [MERGE] Final Tier Resolver - Combine renown + percentile
// ==========================================
export function getEffectiveTier(renown: number, percentile: number, isCreator: boolean = false): TierKey {
  if (isCreator) return 'silenthand'
  if (percentile >= 99.999) return 'silenthand' // #1 holder
  const renownTier = getFixedTierFromRenown(renown)
  const dynamicTier = getTierByPercentile(percentile)

  // Optional: compare rank weights if needed
  const tierOrder = Object.keys(tiers) as TierKey[]
  return tierOrder.indexOf(dynamicTier) > tierOrder.indexOf(renownTier)
    ? dynamicTier
    : renownTier
}

